// ── Shared notification messages ────────────────────────────────────────
// These are exported so UI layers can render consistent copy when they
// surface ForEach validation feedback to the user.

export const FOREACH_SINGLE_ACTION_MESSAGE =
  'ForEach supports multiple actions chained via runAfter.';

export const FOREACH_NESTED_MESSAGE =
  'ForEach loops cannot be nested. Use a sub-workflow for nested iteration.';

export const FOREACH_NESTED_TITLE = 'Nested ForEach not allowed';

export const FOREACH_ACTION_LIMIT_TITLE = 'ForEach action limit';

export const FOREACH_INVALID_STRUCTURE_TITLE = 'Invalid ForEach structure';

// ── Validation ──────────────────────────────────────────────────────────

/**
 * Validates ForEach constraints in a workflow definition:
 * 1. ForEach must not be nested inside another ForEach
 *
 * Recursively traverses all scope-bearing action types (ForEach, If, Switch, Scope, Until).
 */
export function validateForEachConstraints(workflowJson: Record<string, unknown>): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  function checkActions(actions: Record<string, unknown>, path: string, insideForEach = false) {
    for (const [name, action] of Object.entries(actions)) {
      const act = action as Record<string, unknown>;
      const actType = typeof act.type === 'string' ? act.type.toLowerCase() : '';

      if (actType === 'foreach') {
        // Check for nested ForEach
        if (insideForEach) {
          errors.push(
            `ForEach "${path}${name}" is nested inside another ForEach. Nested ForEach loops are not allowed.`
          );
        }

        // Recurse into children with insideForEach=true
        const childActions = act.actions as Record<string, unknown> | undefined;
        if (childActions) checkActions(childActions, `${path}${name}.actions.`, true);
      } else if (act.actions && typeof act.actions === 'object') {
        // Recurse into other scope types (If, Switch, Scope, Until) — pass through current insideForEach
        checkActions(act.actions as Record<string, unknown>, `${path}${name}.actions.`, insideForEach);
      }

      // Handle Switch cases
      const cases = act.cases as Record<string, Record<string, unknown>> | undefined;
      if (cases) {
        for (const [caseName, caseObj] of Object.entries(cases)) {
          const caseActions = caseObj.actions as Record<string, unknown> | undefined;
          if (caseActions) checkActions(caseActions, `${path}${name}.cases.${caseName}.actions.`, insideForEach);
        }
      }

      // Handle Switch default
      const defaultCase = act.default as Record<string, unknown> | undefined;
      if (defaultCase?.actions) {
        checkActions(defaultCase.actions as Record<string, unknown>, `${path}${name}.default.actions.`, insideForEach);
      }

      // Handle If else branch
      const elseBranch = act.else as Record<string, unknown> | undefined;
      if (elseBranch?.actions) {
        checkActions(elseBranch.actions as Record<string, unknown>, `${path}${name}.else.actions.`, insideForEach);
      }

      // Handle Agent tools
      const tools = act.tools as Record<string, Record<string, unknown>> | undefined;
      if (tools) {
        for (const [toolName, toolObj] of Object.entries(tools)) {
          const toolActions = toolObj.actions as Record<string, unknown> | undefined;
          if (toolActions) checkActions(toolActions, `${path}${name}.tools.${toolName}.actions.`, insideForEach);
        }
      }
    }
  }

  // Handle both wrapper and raw formats
  const definition = (workflowJson.definition as Record<string, unknown>) ?? workflowJson;
  const actions = definition.actions as Record<string, unknown> | undefined;
  if (actions) checkActions(actions, '');

  return { valid: errors.length === 0, errors };
}
